import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { COLORS, FONT } from "../../shared/styles";

const INSTALL_INSTRUCTIONS = [
  {
    title: "Shopify",
    description: "Go to Online Store → Themes → Edit code → theme.liquid and paste before </body>.",
  },
  {
    title: "WordPress",
    description: "Install a header/footer script plugin or add the code to your theme footer.",
  },
  {
    title: "Custom CMS",
    description: "Paste the snippet in your site template before the closing </body> tag.",
  },
];

function InstallTab({ snippet, configured = false, groupArchived = false }) {
  const handleCopy = async () => {
    if (groupArchived || !configured || !snippet) return;
    try {
      await navigator.clipboard.writeText(snippet);
      toast.success("Code copied to clipboard.");
    } catch {
      toast.error("Failed to copy code.");
    }
  };

  return (
    <Box>
      <Typography
        sx={{
          color: COLORS.textPrimary,
          fontFamily: FONT.family,
          fontSize: "18px",
          fontWeight: 500,
          lineHeight: "34px",
        }}
      >
        Installation Code
      </Typography>
      <Typography
        sx={{
          mt: "4px",
          color: COLORS.textSecondary,
          fontFamily: FONT.family,
          fontSize: "14px",
          fontWeight: 400,
          lineHeight: "18px",
        }}
      >
        {groupArchived
          ? "Installation code is not available for archived groups"
          : configured
            ? "Copy and paste this code before the closing </body> tag on your website"
            : "Save widget settings for this group to generate your installation code"}
      </Typography>

      <Box
        sx={{
          mt: "16px",
          width: "100%",
          minHeight: "192px",
          borderRadius: "8px",
          background: configured ? COLORS.textPrimary : "#F9FAFA",
          border: configured ? "none" : `1px dashed ${COLORS.border}`,
          p: "16px",
          position: "relative",
          display: "flex",
          alignItems: configured ? "flex-start" : "center",
          justifyContent: configured ? "flex-start" : "center",
        }}
      >
        {configured ? (
          <>
            <Button
              onClick={handleCopy}
              sx={{
                position: "absolute",
                top: "12px",
                right: "12px",
                height: "24px",
                width: "48px",
                borderRadius: "8px",
                background: '#405261',
                color: COLORS.white,
                fontFamily: FONT.family,
                fontSize: "12px",
                fontWeight: 500,
                textTransform: "none",
                boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
                  "&:hover": { background: "#405261" },
              }}
            >
              Copy
            </Button>
            <Box
              component="pre"
              sx={{
                m: 0,
                pr: "80px",
                color: COLORS.white,
                fontFamily: "Consolas, Monaco, monospace",
                fontSize: "12px",
                lineHeight: "20px",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {snippet}
            </Box>
          </>
        ) : (
          <Typography
            sx={{
              maxWidth: "420px",
              textAlign: "center",
              color: COLORS.textSecondary,
              fontFamily: FONT.family,
              fontSize: "14px",
              fontWeight: 400,
              lineHeight: "22px",
              px: "16px",
            }}
          >
            {groupArchived ? (
              <>
                This group is archived. Select an active group to view or copy the
                installation code.
              </>
            ) : (
              <>
                Widget settings have not been saved for this group yet. Customize the
                appearance and behaviour tabs, then click{" "}
                <Box component="span" sx={{ color: COLORS.textPrimary, fontWeight: 500 }}>
                  Save Widget Settings
                </Box>{" "}
                to create your embed code.
              </>
            )}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          mt: "24px",
          width: "100%",
          borderRadius: "8px",
          border: `1px solid ${COLORS.border}`,
          background: COLORS.white,
          p: "16px",
        }}
      >
        {INSTALL_INSTRUCTIONS.map((item, index) => (
          <Box
            key={item.title}
            sx={{
              pb: index < INSTALL_INSTRUCTIONS.length - 1 ? "16px" : 0,
              mb: index < INSTALL_INSTRUCTIONS.length - 1 ? "16px" : 0,
              borderBottom:
                index < INSTALL_INSTRUCTIONS.length - 1
                  ? `1px solid ${COLORS.borderLight}`
                  : "none",
            }}
          >
            <Typography
              sx={{
                color: COLORS.textPrimary,
                fontFamily: FONT.family,
                fontSize: "14px",
                fontWeight: 500,
                lineHeight: "18px",
              }}
            >
              {item.title}
            </Typography>
            <Typography
              sx={{
                mt: "4px",
                color: COLORS.textSecondary,
                fontFamily: FONT.family,
                fontSize: "14px",
                fontWeight: 400,
                lineHeight: "18px",
              }}
            >
              {item.description}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default InstallTab;
